import React from "react";
import { motion } from "framer-motion";
import {
  Video,
  UserRound,
  CalendarCheck,
  Mic,
  Camera,
  ShieldCheck,
  FileText,
  Pill,
  Clock3,
  Check,
  ArrowRight,
  MessageSquare,
  ClipboardList,
  Wifi,
   Stethoscope,
  LockKeyhole,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { useDispatch } from "react-redux";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
} from "../../../Redux/actions/commonActions";

const VirtualCare = () => {

   const dispatch = useDispatch();

  const handleLoginModal = () => {
    dispatch(
      handleLoginSignUpModal({
        data: true,
        actionType: LOGINMODAL,
      })
    );
  };
  const virtualCareFeatures = [
    {
      icon: Video,
      title: "Virtual Consultations",
      description:
        "Connect with patients through supported video-based healthcare consultations.",
    },
    {
      icon: CalendarCheck,
      title: "Virtual Appointment Scheduling",
      description:
        "Schedule virtual appointments alongside your regular healthcare calendar.",
    },
    {
      icon: MessageSquare,
      title: "Patient Communication",
      description:
        "Communicate with patients through supported digital consultation workflows.",
    },
    {
      icon: FileText,
      title: "Clinical Documentation",
      description:
        "Keep relevant consultation notes and patient information organised during care.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: CalendarCheck,
      title: "Schedule Consultation",
      description:
        "Set your availability and schedule a virtual appointment with the patient.",
    },
    {
      number: "02",
      icon: UserRound,
      title: "Join the Consultation",
      description:
        "Connect with your patient through the supported virtual care experience.",
    },
    {
      number: "03",
      icon: ClipboardList,
      title: "Provide Care",
      description:
        "Discuss the patient's concerns and document relevant clinical information.",
    },
    {
      number: "04",
      icon: Check,
      title: "Continue Care",
      description:
        "Follow up with supported prescriptions, appointments and healthcare services.",
    },
  ];

  const virtualServices = [
    {
      icon: Stethoscope,
      title: "Virtual Consultations",
      description:
        "Provide remote consultations through supported video healthcare workflows.",
    },
    {
      icon: MessageSquare,
      title: "Patient Communication",
      description:
        "Keep communication connected before, during and after virtual care.",
    },
    {
      icon: FileText,
      title: "Digital Records",
      description:
        "Connect consultation information with supported digital patient records.",
    },
    {
      icon: Pill,
      title: "e-Prescriptions",
      description:
        "Continue supported prescription workflows after a virtual consultation.",
    },
  ];

  const benefits = [
    "Connect with patients remotely",
    "Schedule and manage virtual appointments",
    "Reduce unnecessary in-person visits where appropriate",
    "Keep consultation information organised",
    "Connect virtual care with digital records",
    "Support follow-up care through connected workflows",
  ];

  const faqs = [
    {
      question: "What is Virtual Care?",
      answer:
        "Virtual Care uses digital technology to support healthcare interactions between patients and healthcare professionals without requiring an in-person visit.",
    },
    {
      question: "Can healthcare professionals conduct video consultations?",
      answer:
        "Where virtual consultation functionality is enabled, healthcare professionals can connect with patients through supported video-based workflows.",
    },
    {
      question: "Can virtual appointments be scheduled?",
      answer:
        "Yes, where scheduling and virtual-care functionality are supported, virtual consultations can be scheduled as part of the appointment workflow.",
    },
    {
      question: "Can consultation notes be stored?",
      answer:
        "Where the relevant EHR and documentation functionality is enabled, healthcare professionals can record supported consultation information.",
    },
    {
      question: "Can e-Prescriptions be created after a virtual consultation?",
      answer:
        "Where e-Prescription functionality is available and clinically appropriate, supported prescriptions can be created as part of the follow-up workflow.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Virtual Care
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Healthcare,
                <span className="block text-teal-500 mt-2">
                  wherever care happens.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Connect with patients remotely through a connected virtual
                care experience designed for modern healthcare professionals.
              </p>

              {/* HERO ACTION */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="flex items-center gap-4">

                  <div className="w-12 h-12 rounded-xl bg-teal-50 grid place-items-center">

                    <Video
                      size={22}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      Virtual Consultation
                    </p>

                    <p className="font-semibold mt-1">
                      Connect with patients remotely
                    </p>

                  </div>
<motion.button
  onClick={handleLoginModal}
  whileHover={{ scale: 1.03 }}
  whileTap={{ scale: 0.97 }}
  className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 h-12 text-sm font-semibold"
>
  Start Virtual Care
  <ArrowRight size={15} />
</motion.button>
                </div>

              <button
  onClick={handleLoginModal}
  className="sm:hidden w-full mt-4 h-12 rounded-xl bg-slate-950 text-white font-semibold text-sm"
>
  Start Virtual Care
</button>

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Secure consultation workflow
                </span>

                <span className="inline-flex items-center gap-2">
                  <Wifi size={16} className="text-teal-500" />
                  Remote healthcare
                </span>

              </div>

            </motion.div>

            {/* RIGHT VIDEO CONSULTATION CARD */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Virtual Consultation
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Patient Visit
                    </h3>

                  </div>

                  <div className="flex items-center gap-2">

                    <span className="w-2 h-2 rounded-full bg-teal-500" />

                    <span className="text-xs font-semibold text-teal-600">
                      Connected
                    </span>

                  </div>

                </div>

                {/* VIDEO AREA */}
                <div className="mt-6 rounded-2xl bg-slate-950 h-56 relative overflow-hidden">

                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-teal-950/60" />

                  {/* DOCTOR */}
                  <div className="absolute left-5 top-5 w-28 h-20 rounded-xl bg-slate-800/90 border border-white/10 flex items-center justify-center">

                    <div className="w-10 h-10 rounded-full bg-teal-500/20 grid place-items-center">

                      <Stethoscope
                        size={19}
                        className="text-teal-300"
                      />

                    </div>

                  </div>

                  {/* PATIENT */}
                  <div className="absolute right-5 bottom-5 w-24 h-16 rounded-xl bg-slate-700/90 border border-white/10 flex items-center justify-center">

                    <UserRound
                      size={20}
                      className="text-slate-300"
                    />

                  </div>

                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">

                    <button className="w-9 h-9 rounded-full bg-white/10 border border-white/10 grid place-items-center">
                      <Mic size={15} className="text-white" />
                    </button>

                    <button className="w-9 h-9 rounded-full bg-teal-500 grid place-items-center">
                      <Camera size={15} className="text-white" />
                    </button>

                    <button className="w-9 h-9 rounded-full bg-white/10 border border-white/10 grid place-items-center">
                      <MessageSquare
                        size={15}
                        className="text-white"
                      />
                    </button>

                  </div>

                </div>

                {/* PATIENT INFO */}
                <div className="mt-5 flex items-center gap-4 rounded-2xl bg-slate-50 p-4">

                  <div className="w-11 h-11 rounded-xl bg-teal-100 grid place-items-center">

                    <UserRound
                      size={19}
                      className="text-teal-700"
                    />

                  </div>

                  <div className="flex-1">

                    <p className="text-xs font-semibold">
                      Patient Consultation
                    </p>

                    <p className="text-[10px] text-slate-400 mt-1">
                      Virtual appointment
                    </p>

                  </div>

                  <Clock3
                    size={17}
                    className="text-teal-600"
                  />

                </div>

                {/* STATUS */}
                <div className="mt-4 rounded-2xl bg-teal-50 p-4">

                  <div className="flex items-center gap-3">

                    <div className="w-9 h-9 rounded-xl bg-white grid place-items-center">

                      <ShieldCheck
                        size={17}
                        className="text-teal-600"
                      />

                    </div>

                    <div>

                      <p className="text-xs font-semibold">
                        Consultation in progress
                      </p>

                      <p className="text-[10px] text-slate-400 mt-1">
                        Virtual care session
                      </p>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Virtual Care Features
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Bring care closer,
                <span className="text-teal-500">
                  {" "}without the commute.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Support remote healthcare interactions while keeping
                appointments, communication and clinical information
                connected.
              </p>

            </div>

           <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {virtualCareFeatures.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
          <Icon
            size={17}
            className="text-teal-600 group-hover:text-white transition"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

        {/* <div className="inline-flex items-center gap-1.5 text-teal-600 text-[11px] font-semibold mt-3">
          Explore
          <ArrowRight size={12} />
        </div> */}

      </motion.div>
    );
  })}

</div>

          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Virtual care made
                <span className="text-teal-500">
                  {" "}simple.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            SERVICES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Connected Virtual Care
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                More than a video call.
                <span className="block text-teal-500">
                  A complete care journey.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Connect virtual consultations with the healthcare workflows
                that support patient care before and after the visit.
              </p>

            </div>

           <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {virtualServices.map((item, index) => {

    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.06 }}
        whileHover={{ y: -4 }}
        className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center">
          <Icon
            size={17}
            className="text-teal-600"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

      </motion.div>
    );
  })}

</div>

          </div>

        </section>

        {/* =====================================================
            PROVIDER WORKFLOW
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Connected Healthcare
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Virtual care that fits
                <span className="block text-teal-400">
                  into your workflow.
                </span>

              </h2>

              <p className="text-slate-400 text-lg leading-8 mt-6 max-w-xl">
                Connect consultations with scheduling, patient records,
                clinical documentation and supported prescriptions.
              </p>

            </div>

            <div className="space-y-3">

              {[
                [CalendarCheck, "Virtual Appointments"],
                [FileText, "Digital Patient Records"],
                [ClipboardList, "Clinical Documentation"],
                [Pill, "e-Prescriptions"],
              ].map(([Icon, title], index) => (

                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Icon
                      size={18}
                      className="text-teal-400"
                    />

                  </div>

                  <span className="text-sm font-medium text-slate-300 flex-1">
                    {title}
                  </span>

                  <ArrowRight
                    size={16}
                    className="text-slate-500"
                  />

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6">

            <div className="rounded-[32px] bg-[#f6fbfc] border border-teal-50 p-8 md:p-12 grid lg:grid-cols-2 gap-12 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Privacy & Security
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Virtual care should feel
                  <span className="block text-teal-500">
                    secure and private.
                  </span>

                </h2>

                <p className="text-slate-600 leading-8 mt-6">
                  Virtual healthcare involves sensitive patient information.
                  Appropriate privacy, security, authentication and access
                  controls should be applied according to the implementation
                  and applicable requirements.
                </p>

              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">

                {[
                  [ShieldCheck, "Secure Sessions"],
                  [LockKeyhole, "Access Controls"],
                  [FileText, "Digital Records"],
                  [Wifi, "Reliable Connection"],
                ].map(([Icon, title], index) => (

                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    className="rounded-2xl bg-white border border-slate-100 p-5"
                  >

                    <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={18}
                        className="text-teal-600"
                      />

                    </div>

                    <p className="font-semibold text-sm mt-4">
                      {title}
                    </p>

                  </motion.div>

                ))}

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Provider Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Extend your care
                <span className="block text-teal-500">
                  beyond the clinic.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6 max-w-xl">
                Give patients another way to connect with healthcare
                professionals when virtual care is appropriate.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Bring healthcare
                <span className="text-teal-400">
                  {" "}closer to patients.
                </span>

              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Connect patients and healthcare professionals through
                supported virtual consultation and remote care workflows.
              </p>
<button
  onClick={handleLoginModal}
  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
>
  Explore Virtual Care
  <ArrowRight size={17} />
</button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default VirtualCare;